import React, {Suspense, useEffect} from 'react';
import {Routes, Route, useLocation} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';

import CacheBuster from 'react-cache-buster';
import packageInfo from '../package.json';

import {initGA, disableGA} from './app/utils';

import ProtectedRoute from './components/ProtectedRoute';
import {RouteFallback} from './components/RouteFallback';
import BackendRoute from './components/BackendRoute';
import {Home} from './features/Home';

import {Login} from './features/Login';
import {Error} from './features/Error';

// Route-level code splitting. Every feature below pulls in a heavy dependency tree (deck.gl for
// the map views, Chart.js for Statsview, the Settings tab tree) that otherwise ships in the
// single app bundle to every user on every page. `lazyFeature` adapts our named feature exports
// to the default export React.lazy expects.
function lazyFeature(loader, exportName) {
  return React.lazy(async () => {
    const mod = await loader();
    return {default: mod[exportName]};
  });
}

const Shopview = lazyFeature(() => {
  return import('./features/Shopview');
}, 'Shopview');
const Cabview = lazyFeature(() => {
  return import('./features/Cabview');
}, 'Cabview');
const Landing = lazyFeature(() => {
  return import('./features/Landing');
}, 'Landing');
const Cropview = lazyFeature(() => {
  return import('./features/Cropview');
}, 'Cropview');
const Mapview = lazyFeature(() => {
  return import('./features/Mapview');
}, 'Mapview');
const InspectionFormEntry = lazyFeature(() => {
  return import('./features/InspectionForm/InspectionFormEntry');
}, 'InspectionFormEntry');
const BreakdownForm = lazyFeature(() => {
  return import('./features/BreakdownForm');
}, 'BreakdownForm');
const Statsview = lazyFeature(() => {
  return import('./features/Statsview');
}, 'Statsview');
const ReportBuilder = lazyFeature(() => {
  return import('./features/ReportBuilder');
}, 'ReportBuilder');
const PublishedReport = lazyFeature(() => {
  return import('./features/ReportBuilder');
}, 'PublishedReport');
const PrintReport = lazyFeature(() => {
  return import('./features/ReportBuilder');
}, 'PrintReport');
const RetiredReport = lazyFeature(() => {
  return import('./features/ReportBuilder');
}, 'RetiredReport');
const Alertview = lazyFeature(() => {
  return import('./features/Alertview');
}, 'Alertview');
const Insights = lazyFeature(() => {
  return import('./features/Insights');
}, 'Insights');
const Extensions = lazyFeature(() => {
  return import('./features/Extensions');
}, 'Extensions');
const Labor = lazyFeature(() => {
  return import('./features/Labor');
}, 'Labor');
const CreateUser = lazyFeature(() => {
  return import('./features/CreateUser');
}, 'CreateUser');
const InviteUser = lazyFeature(() => {
  return import('./features/InviteUser');
}, 'InviteUser');
const DeleteAccount = lazyFeature(() => {
  return import('./features/DeleteAccount');
}, 'DeleteAccount');
const CreateAccount = lazyFeature(() => {
  return import('./features/CreateAccount');
}, 'CreateAccount');
const ForgotPassword = lazyFeature(() => {
  return import('./features/ForgotPassword');
}, 'ForgotPassword');
const ResetPassword = lazyFeature(() => {
  return import('./features/ResetPassword');
}, 'ResetPassword');

const Settings = lazyFeature(() => {
  return import('./features/Settings');
}, 'Settings');
const PersonalSettings = lazyFeature(() => {
  return import('./features/PersonalSettings');
}, 'PersonalSettings');
const AuditLogs = lazyFeature(() => {
  return import('./features/AuditLogs');
}, 'AuditLogs');

import {setIsUserLanguageSwitchAvailable} from './appSlice';

// Amplitude
import * as amplitude from '@amplitude/analytics-browser';
import {autocapturePlugin} from '@amplitude/plugin-autocapture-browser';
import {plugin as engagementPlugin} from '@amplitude/engagement-browser';
const AMPLITUDE_API_KEY = '4f250eb4b6b7135217bd17cb35c89d5d';

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const {t, i18n} = useTranslation();

  const reloadApp = useSelector((state) => {
    return state.app.reloadApp;
  });
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });

  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });

  function initAmplitude() {
    let paddedUsername = userSettings.username;
    if (paddedUsername.length < 5) {
      paddedUsername = paddedUsername + '_____';
    }

    if (authenticationStatus.env == 'production') {
      amplitude.init(AMPLITUDE_API_KEY, paddedUsername, {
        autocapture: {
          attribution: true,
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: true,
          elementInteractions: true,
        },
      });
      amplitude.add(engagementPlugin());
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set('email', userSettings.email);
      identifyEvent.set('environment', authenticationStatus.env);
      identifyEvent.set('role', userSettings.role);
      identifyEvent.set('databasename', userSettings.databaseName);
      identifyEvent.set('pipedriveid', customerSettings.general.pipedriveOrganizationId);
      amplitude.identify(identifyEvent);
      amplitude.setGroup('databasename', userSettings.databaseName);

      const databaseGroupIdentify = new amplitude.Identify()
        .set('databasename', userSettings.databaseName)
        .set('role', userSettings.role)
        .set('pipedriveid', customerSettings.general.pipedriveOrganizationId);

      amplitude.groupIdentify('databasename', userSettings.databaseName, databaseGroupIdentify);
      amplitude.groupIdentify('role', userSettings.role, databaseGroupIdentify);
      amplitude.groupIdentify('pipedriveid', customerSettings.general.pipedriveOrganizationId, databaseGroupIdentify);
    }
  }

  useEffect(() => {
    if (typeof userSettings != 'undefined' && userSettings.ignoreProcess === false) {
      initAmplitude();
      initGA();
    } else {
      disableGA();
    }
    const languageSwitchAvailable =
      location.pathname == '/landing' ||
      location.pathname == '/inspectionForm' ||
      location.pathname == '/breakdownForm' ||
      location.pathname == '/shopview' ||
      location.pathname == '/labor' ||
      location.pathname == '/cabview' ||
      location.pathname == '/error';
    dispatch(setIsUserLanguageSwitchAvailable(languageSwitchAvailable));
  }, [location, userSettings]);

  useEffect(() => {
    i18n.changeLanguage(userSettings.language);
  }, [userSettings.language]);

  useEffect(() => {
    if (reloadApp) {
      alert('New app version available. Reloading app');
      window.location.reload(true);
    }
  }, [reloadApp]);

  return (
    <CacheBuster
      currentVersion={packageInfo.version}
      isEnabled={true} // If false, the library is disabled.
      isVerboseMode={true} // If true, the library writes verbose logs to console.
    >
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path='/login' element={<Login />} />
          <Route path='/r/:customer/:token' element={<PublishedReport />} />
          <Route path='/r/:token' element={<RetiredReport />} />
          <Route path='/rb/print' element={<PrintReport />} />
          <Route path='/create_account' element={<CreateAccount />} />
          <Route path='/forgot_password' element={<ForgotPassword />} />
          <Route path='/reset_password' element={<ResetPassword />} />
          <Route path='/error' element={<Error />} />
          <Route element={<ProtectedRoute />}>
            <Route exact path='/' element={<Home />} />
            <Route path='/shopview' element={<Shopview />} />
            <Route path='/cabview' element={<Cabview />} />
            <Route path='/landing' element={<Landing />} />
            <Route path='/cropview' element={<Cropview />} />
            <Route path='/statsview' element={<Statsview />} />
            <Route path='/reportbuilder' element={<ReportBuilder />} />
            <Route path='/alertview' element={<Alertview />} />
            <Route path='/insights' element={<Insights />} />
            <Route path='/extensions/:slug' element={<Extensions />} />
            <Route path='/mapview' element={<Mapview />} />
            <Route path='/inspectionForm' element={<InspectionFormEntry />} />
            <Route path='/breakdownForm' element={<BreakdownForm />} />
            <Route path='/labor' element={<Labor />} />
            <Route path='/delete_account' element={<DeleteAccount />} />
            <Route path='/personal_settings' element={<PersonalSettings />} />
            <Route path='/organization_settings' element={<Settings />} />
            <Route path='/audit_logs' element={<AuditLogs />} />
          </Route>
          <Route element={<ProtectedRoute adminReq={true} />}>
            <Route path='/create_user' element={<CreateUser />} />
            <Route path='/invite_user' element={<InviteUser />} />
          </Route>

          {/* Routes only defined for react development server */}
          {window.location.port == 3000 && (
            <React.Fragment>
              <Route element={<ProtectedRoute />}>
                <Route path='/settings' element={<BackendRoute />} />
                <Route path='/scan' element={<BackendRoute />} />
                <Route path='/files' element={<BackendRoute />} />
              </Route>
              <Route path='/logout' element={<BackendRoute />} />
            </React.Fragment>
          )}

          <Route path='*' element={<Error />} />
        </Routes>
      </Suspense>
    </CacheBuster>
  );
}

export default App;
